import { useMemo } from "react";
import { usePostGetAllPizzas } from "@/api/pizza-checker/pizzas/pizzas";
import type { PizzaDTO, PizzasDTO } from "@/api/custom-schema";
import { usePermissions } from "@/hooks/use-permissions";

/**
 * Captured pizzas of a set of order items, in one request (an order tile or list row needs the verdict
 * and the pizza ROI of each item). Items without a capture have no entry.
 */
export function useOrderPizzas(itemIds: string[]) {
  const { hasPermission } = usePermissions();
  const ids = useMemo(() => [...new Set(itemIds.filter(Boolean))].sort(), [itemIds]);
  const { data, isLoading } = usePostGetAllPizzas<PizzasDTO>(
    {
      skip: 0,
      limit: Math.max(ids.length, 1),
      filters: { id: { $in: ids } },
    },
    {
      query: {
        enabled: ids.length > 0 && (hasPermission("pizzas") || hasPermission("pizzas.branch")),
        staleTime: 30 * 1000,
      },
    },
  );

  const byId = useMemo(() => {
    const map = new Map<string, PizzaDTO>();
    for (const pizza of data?.pizzas ?? []) map.set(pizza.id, pizza);
    return map;
  }, [data]);

  return { pizzasById: byId, isLoading: isLoading && ids.length > 0 };
}
